import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { styled, useTheme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { invalidateUserSessionCredits } from "../../hooks/useAuth";
import { partyShellIdToModePathSegment } from "../../modes/shellToModeApi";
import { LongPressWordPeek } from "../../party/LongPressWordPeek";
import { partyShellSupportsNativeTvCast } from "../../party/modePartyCards";
import { buildPartyCastRevealUrl } from "../../party/partyCastPayload";
import PartyCastQrPanel from "../../party/PartyCastQrPanel";
import PartyCastSourceToggle from "../../party/PartyCastSourceToggle";
import PartyShareResultButton from "../../party/share/PartyShareResultButton";
import { usePartyShareModeTitle } from "../../party/share/usePartyShareModeTitle";
import { PartyTopBar } from "../../party/PartyChrome";
import { PartyMascot } from "../../party/PartyMascot";
import { handlePartyTeamNameEnterFocusNext, isPartyNameSubmitKey } from "../../party/partyNameInputEnter";
import { PartySavedRosterSetupSection } from "../../party/PartySavedRosterSetupSection";
import { PartyPlayResumePrompt } from "../../party/PartyPlayResumePrompt";
import { PartySuggestCategoryDrawer } from "../../party/PartySuggestCategoryDrawer";
import { usePartySavedRoster } from "../../party/usePartySavedRoster";
import { getTrialWhoAmIWordsSplit } from "../../party/partyTrialMockData";
import { PartyTrialSetupBanner } from "../../party/PartyTrialSetupBanner";
import { usePartyPlayTrialMode } from "../../party/usePartyPlayTrialMode";
import type { PartyShellPalette } from "../../theme/theme";
import type { ModeCategoryRow } from "../../types/api";
import { apiClient } from "../../utils/api";
import { trackPartyGameCompleted, trackPartyGameStarted } from "../../utils/mixpanel";
import { modeCategoryDisplayName, modeCategoryPrimaryName } from "../../utils/modeCategoryLabels";
import { clearPartyGameCache, readPartyGameCache, writePartyGameCache } from "../../utils/partyGameLocalCache";

const SEG = partyShellIdToModePathSegment("who");
const CACHE_SHELL = "who";

type Phase = "pick" | "round" | "score" | "end";
type WordRevealStep = "a" | "b" | "bridge";

type WhoAmICacheData = {
  phase: Phase;
  teamA: string;
  teamB: string;
  wordsA: string[];
  wordsB: string[];
  round: number;
  scoreA: number;
  scoreB: number;
  wordRevealStep: WordRevealStep;
};
type TeamSide = "a" | "b";
type FinalPanelVariant = "winnerA" | "loserOrTieA" | "winnerB" | "loserOrTieB";

// --- styled -----------------------------------------------------------------

const SetupRoot = styled(Box)({
  padding: "4px 20px 100px",
});

const RoundRoot = styled(Box)({
  padding: 20,
});

const PickHint = styled(Typography)({
  display: "block",
  color: "var(--ink-dim)",
  fontSize: 14,
  marginBottom: 12,
});

const TeamsRow = styled(Stack)({
  flexDirection: "row",
  gap: 8,
  marginBottom: 12,
});

const TeamField = styled(TextField)({
  flex: 1,
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    backgroundColor: "rgba(255,255,255,0.05)",
    color: "var(--ink)",
    "& fieldset": {
      borderColor: "var(--ink-faint)",
      borderWidth: 1.5,
    },
    "&:hover fieldset": {
      borderColor: "var(--ink-faint)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "var(--hot)",
    },
  },
});

const CategoryPickButton = styled(Button)({
  width: "100%",
  marginBottom: 8,
  padding: "14px 16px",
  textAlign: "start",
  justifyContent: "flex-start",
  textTransform: "none",
  fontWeight: 700,
});

const TeamsWarning = styled(Typography)({
  display: "block",
  color: "var(--hot)",
  fontSize: 13,
  marginBottom: 10,
});

const CreditErrorText = styled(Typography)({
  display: "block",
  color: "var(--hot)",
  marginTop: 8,
});

const SectionTitle = styled(Typography)({
  fontSize: "clamp(22px, 5vw, 28px)",
  marginBottom: 8,
});

const SectionBody = styled(Typography)({
  display: "block",
  color: "var(--ink-dim)",
  fontSize: 14,
  marginBottom: 14,
  lineHeight: 1.5,
});

const PeekCard = styled(Box)({
  "--card-bg": "var(--paper)",
  color: "var(--paper-ink)",
  padding: 24,
  marginBottom: 16,
  borderRadius: "inherit",
});

/** Large, high-contrast team label above the long-press word area */
const PeekCardTeamHeading = styled(Box, {
  shouldForwardProp: (p) => p !== "team",
})<{ team: TeamSide }>(({ team }) => ({
  fontFamily: "var(--font-display)",
  fontWeight: 900,
  fontSize: "clamp(24px, 6.5vw, 34px)",
  lineHeight: 1.12,
  letterSpacing: "-0.02em",
  marginBottom: 20,
  paddingBottom: 16,
  textAlign: "center",
  color: team === "a" ? "var(--mint)" : "var(--gold)",
  borderBottom:
    team === "a"
      ? "3px solid color-mix(in oklab, var(--mint) 55%, transparent)"
      : "3px solid color-mix(in oklab, var(--gold) 55%, transparent)",
  textShadow:
    team === "a"
      ? "0 0 32px rgba(120, 255, 214, 0.28)"
      : "0 0 32px rgba(255, 200, 100, 0.22)",
}));

const PrimaryCta = styled(Button)({
  width: "100%",
});

const ScoreHeading = styled(Typography)({
  fontSize: 17,
  fontWeight: 800,
  marginBottom: 16,
  lineHeight: 1.35,
});

const ScoreTeamButton = styled(Button)({
  width: "100%",
  marginBottom: 8,
  textTransform: "none",
  fontWeight: 800,
  fontSize: 16,
  padding: "14px 16px",
});

const EndRoot = styled(Box)({
  padding: "16px 20px 100px",
  textAlign: "center",
  minHeight: "100dvh",
  boxSizing: "border-box",
});

const MascotWrap = styled(Box)({
  marginTop: 8,
  marginBottom: 16,
});

const WinnerKicker = styled(Box)({
  fontSize: 12,
  letterSpacing: "0.2em",
  color: "var(--hot)",
  fontWeight: 800,
  textShadow: "0 0 24px rgba(255, 61, 120, 0.35)",
});

const WinnerName = styled(Box, {
  shouldForwardProp: (p) => p !== "winnerIsA",
})<{ winnerIsA: boolean }>(({ winnerIsA }) => ({
  fontSize: "clamp(36px, 11vw, 52px)",
  lineHeight: 1.05,
  marginTop: 10,
  color: winnerIsA ? "var(--mint)" : "var(--gold)",
  textShadow: winnerIsA
    ? "0 0 60px -10px rgba(120, 255, 214, 0.45)"
    : "0 0 60px -10px rgba(255, 200, 100, 0.4)",
}));

const WinnerSub = styled(Box)({
  fontSize: 14,
  color: "var(--ink-dim)",
  marginTop: 10,
  fontWeight: 700,
});

const TieOverline = styled(Box)({
  fontSize: 12,
  letterSpacing: "0.12em",
  color: "var(--ink-dim)",
});

const TieTitle = styled(Box)({
  fontSize: "clamp(32px, 9vw, 44px)",
  marginTop: 8,
  color: "var(--ink)",
});

const FinalScoreGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "1fr auto 1fr",
  alignItems: "stretch",
  gap: 12,
  marginTop: 28,
  maxWidth: 420,
  marginLeft: "auto",
  marginRight: "auto",
});

const FinalScoreCell = styled(Box, {
  shouldForwardProp: (p) => p !== "panelVariant",
})<{ panelVariant: FinalPanelVariant }>(({ panelVariant }) => {
  const isWinnerA = panelVariant === "winnerA";
  const isWinnerB = panelVariant === "winnerB";
  return {
    padding: "16px 14px",
    borderRadius: 18,
    border: isWinnerA
      ? "2px solid color-mix(in oklab, var(--mint) 65%, transparent)"
      : isWinnerB
        ? "2px solid color-mix(in oklab, var(--gold) 65%, transparent)"
        : "1.5px solid var(--ink-faint)",
    background: isWinnerA
      ? "color-mix(in oklab, var(--mint) 10%, transparent)"
      : isWinnerB
        ? "color-mix(in oklab, var(--gold) 12%, transparent)"
        : "rgba(255,255,255,0.04)",
    boxShadow: isWinnerA
      ? "0 12px 32px -14px rgba(120, 255, 214, 0.25)"
      : isWinnerB
        ? "0 12px 32px -14px rgba(255, 200, 100, 0.22)"
        : "none",
  };
});

const FinalTeamTitle = styled(Box)({
  fontSize: 10,
  color: "var(--ink-dim)",
});

const FinalTeamName = styled(Box)({
  fontSize: 13,
  fontWeight: 800,
  color: "var(--ink)",
  marginTop: 4,
  lineHeight: 1.2,
  minHeight: "2.4em",
});

const FinalScoreNumber = styled(Box, {
  shouldForwardProp: (p) => p !== "team",
})<{ team: TeamSide }>(({ team }) => ({
  fontSize: 44,
  marginTop: 8,
  lineHeight: 1,
  color: team === "a" ? "var(--mint)" : "var(--gold)",
}));

const FinalPointsLabel = styled(Box)({
  fontSize: 10,
  color: "var(--ink-dim)",
  marginTop: 4,
});

const ScoreColon = styled(Box)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: 28,
  color: "var(--ink-dim)",
  opacity: 0.85,
  fontWeight: 900,
}));

const ColonWrap = styled(Stack)({
  alignItems: "center",
  justifyContent: "center",
});

const HomeButton = styled(Button)({
  width: "100%",
  maxWidth: 420,
  marginTop: 28,
  marginLeft: "auto",
  marginRight: "auto",
  display: "block",
});

// --- screen -----------------------------------------------------------------

export default function PartyPlayWhoAmI() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const partyPal = (theme.palette as unknown as { party: PartyShellPalette }).party;
  const shareModeTitle = usePartyShareModeTitle("who", i18n.language?.startsWith("ar") ? "من أنا" : "Who am I");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isTrialMode = usePartyPlayTrialMode();
  const savedRoster = usePartySavedRoster();
  const [phase, setPhase] = useState<Phase>("pick");
  const [teamA, setTeamA] = useState("");
  const [teamB, setTeamB] = useState("");
  const [wordsA, setWordsA] = useState<string[]>([]);
  const [wordsB, setWordsB] = useState<string[]>([]);
  const [round, setRound] = useState(0);
  const [scoreA, setScoreA] = useState(0);
  const [scoreB, setScoreB] = useState(0);
  const [gameStartedAtMs, setGameStartedAtMs] = useState<number | null>(null);
  const completionTrackedRef = useRef(false);
  const [wordRevealStep, setWordRevealStep] = useState<WordRevealStep>("a");
  const [castByQr, setCastByQr] = useState(false);
  const [suggestCategoryOpen, setSuggestCategoryOpen] = useState(false);
  const nativeTvCast = partyShellSupportsNativeTvCast(CACHE_SHELL);
  const roundAnchorRef = useRef<number | null>(null);
  const teamBFieldRef = useRef<HTMLInputElement | null>(null);
  const teamARef = useRef(teamA);
  const teamBRef = useRef(teamB);
  useEffect(() => {
    teamARef.current = teamA;
    teamBRef.current = teamB;
  }, [teamA, teamB]);

  const [cacheEnvelope] = useState(() => readPartyGameCache<WhoAmICacheData>(CACHE_SHELL));
  const [resumeDismissed, setResumeDismissed] = useState(false);
  const cachedWhoProgress = (() => {
    if (!cacheEnvelope?.data) return false;
    const d = cacheEnvelope.data;
    if (d.phase === "end") return false;
    if (d.phase === "round" || d.phase === "score") return true;
    if (d.phase === "pick") {
      return Boolean(d.teamA?.trim() || d.teamB?.trim() || (d.wordsA?.length ?? 0) > 0);
    }
    return false;
  })();
  const showResumePrompt = Boolean(cachedWhoProgress && !resumeDismissed);

  useEffect(() => {
    if (phase !== "round") {
      roundAnchorRef.current = null;
      return;
    }
    if (roundAnchorRef.current !== round) {
      setWordRevealStep("a");
      roundAnchorRef.current = round;
    }
  }, [phase, round]);

  const catQ = useQuery({
    queryKey: ["modes", SEG, "categories"],
    queryFn: () => apiClient.getModeCategories(SEG),
    enabled: phase === "pick" && !isTrialMode,
  });

  const startMut = useMutation({
    mutationFn: ({ categoryId }: { categoryId: number; category_names: string[] }) =>
      apiClient.postWhoAmIStart(categoryId),
    onSuccess: (data: { contents?: { word?: string }[] }, variables) => {
      savedRoster.recordTeams(teamARef.current, teamBRef.current);
      const contents = data?.contents ?? [];
      const words = contents.map((c) => String(c.word ?? ""));
      const half = Math.ceil(words.length / 2);
      setWordsA(words.slice(0, half));
      setWordsB(words.slice(half));
      setRound(0);
      setPhase("round");
      setGameStartedAtMs(Date.now());
      completionTrackedRef.current = false;
      invalidateUserSessionCredits(queryClient);
      trackPartyGameStarted({
        shell_id: "who",
        category_names: variables.category_names,
        number_of_players: 2,
        trial: false,
        mode_name_en: "Who am I",
        team_a_name: teamA.trim() || undefined,
        team_b_name: teamB.trim() || undefined,
        deck_size: words.length,
      });
    },
  });

  const beginWhoAmITrial = useCallback(() => {
    savedRoster.recordTeams(teamARef.current, teamBRef.current);
    const { wordsA, wordsB } = getTrialWhoAmIWordsSplit();
    setWordsA(wordsA);
    setWordsB(wordsB);
    setRound(0);
    setPhase("round");
    setGameStartedAtMs(Date.now());
    completionTrackedRef.current = false;
    const w = [...wordsA, ...wordsB];
    trackPartyGameStarted({
      shell_id: "who",
      category_names: ["trial_sample"],
      number_of_players: 2,
      trial: true,
      mode_name_en: "Who am I",
      team_a_name: teamA.trim() || undefined,
      team_b_name: teamB.trim() || undefined,
      deck_size: w.length,
    });
  }, [savedRoster, teamA, teamB]);

  const wA = wordsA[Math.min(round, wordsA.length - 1)] ?? "";
  const wB = wordsB[Math.min(round, wordsB.length - 1)] ?? "";
  const teamALabel = teamA.trim() || t("party.playWhoAmI.defaultTeamA");
  const teamBLabel = teamB.trim() || t("party.playWhoAmI.defaultTeamB");
  const teamsReady = teamA.trim().length > 0 && teamB.trim().length > 0;

  const awardRound = (winner: TeamSide) => {
    if (winner === "a") setScoreA((s) => s + 1);
    else setScoreB((s) => s + 1);
    setRound((r) => {
      const nr = r + 1;
      setPhase(nr >= 3 ? "end" : "round");
      return nr;
    });
  };

  useEffect(() => {
    if (phase === "end") {
      clearPartyGameCache(CACHE_SHELL);
      return;
    }
    const id = window.setTimeout(() => {
      const payload: WhoAmICacheData = {
        phase,
        teamA,
        teamB,
        wordsA,
        wordsB,
        round,
        scoreA,
        scoreB,
        wordRevealStep,
      };
      writePartyGameCache(CACHE_SHELL, payload);
    }, 400);
    return () => window.clearTimeout(id);
  }, [phase, teamA, teamB, wordsA, wordsB, round, scoreA, scoreB, wordRevealStep]);

  useEffect(() => {
    if (phase !== "end" || gameStartedAtMs == null || completionTrackedRef.current) return;
    completionTrackedRef.current = true;
    trackPartyGameCompleted({
      shell_id: "who",
      trial: isTrialMode,
      mode_name_en: "Who am I",
      duration_seconds: Math.max(0, Math.round((Date.now() - gameStartedAtMs) / 1000)),
    });
  }, [gameStartedAtMs, isTrialMode, phase]);

  const applyWhoAmICache = useCallback((data: WhoAmICacheData) => {
    const r = typeof data.round === "number" ? data.round : 0;
    if (r >= 3) {
      setPhase("end");
      setRound(r);
      setTeamA(typeof data.teamA === "string" ? data.teamA : "");
      setTeamB(typeof data.teamB === "string" ? data.teamB : "");
      setWordsA(Array.isArray(data.wordsA) ? data.wordsA : []);
      setWordsB(Array.isArray(data.wordsB) ? data.wordsB : []);
      setScoreA(typeof data.scoreA === "number" ? data.scoreA : 0);
      setScoreB(typeof data.scoreB === "number" ? data.scoreB : 0);
      setWordRevealStep("a");
      return;
    }
    const ph: Phase = data.phase === "pick" || data.phase === "round" || data.phase === "score" ? data.phase : "pick";
    setPhase(ph);
    setTeamA(typeof data.teamA === "string" ? data.teamA : "");
    setTeamB(typeof data.teamB === "string" ? data.teamB : "");
    setWordsA(Array.isArray(data.wordsA) ? data.wordsA : []);
    setWordsB(Array.isArray(data.wordsB) ? data.wordsB : []);
    setRound(r);
    setScoreA(typeof data.scoreA === "number" ? data.scoreA : 0);
    setScoreB(typeof data.scoreB === "number" ? data.scoreB : 0);
    const wr = data.wordRevealStep;
    setWordRevealStep(wr === "a" || wr === "b" || wr === "bridge" ? wr : "a");
  }, []);

  if (phase === "pick") {
    return (
      <SetupRoot>
        <PartyTopBar title={t("party.playWhoAmI.setupTitleBar")} onBack={() => navigate("/party/detail/who")} />
        <Stack alignItems="center" py={1.5}>
          <PartyMascot shellId="who" size={100} />
        </Stack>
        <TeamsRow>
          <TeamField
            size="small"
            value={teamA}
            onChange={(e) => setTeamA(e.target.value)}
            onKeyDown={(e) => handlePartyTeamNameEnterFocusNext(e, teamBFieldRef.current)}
            placeholder={t("party.playWhoAmI.teamAPlaceholder")}
            hiddenLabel
            InputProps={{ "aria-label": t("party.playWhoAmI.teamAPlaceholder") }}
          />
          <TeamField
            size="small"
            value={teamB}
            onChange={(e) => setTeamB(e.target.value)}
            onKeyDown={(e) => {
              if (!isPartyNameSubmitKey(e)) return;
              e.preventDefault();
            }}
            inputRef={teamBFieldRef}
            placeholder={t("party.playWhoAmI.teamBPlaceholder")}
            hiddenLabel
            InputProps={{ "aria-label": t("party.playWhoAmI.teamBPlaceholder") }}
          />
        </TeamsRow>
        <PartySavedRosterSetupSection
          api={savedRoster}
          variant="teams"
          onApplySavedTeams={(a, b) => {
            setTeamA(a);
            setTeamB(b);
          }}
        />
        {isTrialMode && <PartyTrialSetupBanner />}
        <PickHint>{isTrialMode ? t("party.playWhoAmI.pickHintTrial") : t("party.playWhoAmI.pickHint")}</PickHint>
        <PartyCastSourceToggle
          nativeTvSupported={nativeTvCast}
          castByQr={castByQr}
          onChange={setCastByQr}
          partyPal={partyPal}
        />
        {!teamsReady && (
          <TeamsWarning>
            {isTrialMode ? t("party.playWhoAmI.teamsRequiredWarningTrial") : t("party.playWhoAmI.teamsRequiredWarning")}
          </TeamsWarning>
        )}
        {showResumePrompt && cacheEnvelope ? (
          <PartyPlayResumePrompt
            savedAtIso={cacheEnvelope.savedAt}
            onContinue={() => {
              applyWhoAmICache(cacheEnvelope.data);
              setResumeDismissed(true);
            }}
            onNewGame={() => {
              clearPartyGameCache(CACHE_SHELL);
              setResumeDismissed(true);
            }}
          />
        ) : (
          <Stack spacing={1.25} sx={{ width: "100%" }}>
            {!isTrialMode &&
              (catQ.data ?? []).map((c: ModeCategoryRow) => (
                <CategoryPickButton
                  key={c.id}
                  type="button"
                  variant="outlined"
                  disabled={!teamsReady || startMut.isPending}
                  className="party-btn party-btn-paper"
                  onClick={() =>
                    startMut.mutate({
                      categoryId: c.id,
                      category_names: [modeCategoryPrimaryName(c)],
                    })
                  }
                >
                  {modeCategoryDisplayName(c, i18n.language)}
                </CategoryPickButton>
              ))}
            {isTrialMode && (
              <CategoryPickButton
                type="button"
                variant="outlined"
                disabled={!teamsReady}
                className="party-btn party-btn-hot"
                onClick={beginWhoAmITrial}
              >
                {t("party.trial.continueSampleCta")}
              </CategoryPickButton>
            )}
          </Stack>
        )}
        {!isTrialMode && (startMut.error as { response?: { status?: number } })?.response?.status === 402 && (
          <CreditErrorText>{t("party.playWhoAmI.creditInsufficient")}</CreditErrorText>
        )}
        {!isTrialMode && (
          <>
            <Button
              type="button"
              variant="text"
              onClick={() => setSuggestCategoryOpen(true)}
              sx={{ mt: 1, color: "var(--hot)", fontWeight: 800, textTransform: "none", display: "block", mx: "auto" }}
            >
              {t("party.suggestCategory.cta")}
            </Button>
            <PartySuggestCategoryDrawer
              open={suggestCategoryOpen}
              onClose={() => setSuggestCategoryOpen(false)}
              gameKey="whoAmI"
              partyPal={partyPal}
            />
          </>
        )}
      </SetupRoot>
    );
  }

  if (phase === "round" && round < 3) {
    const roundCurrent = round + 1;
    const roundTotal = 3;

    if (wordRevealStep === "a") {
      return (
        <RoundRoot>
          <PartyTopBar
            title={t("party.playWhoAmI.revealTopBar", {
              current: roundCurrent,
              total: roundTotal,
              team: teamALabel,
            })}
            onBack={() => setPhase("pick")}
          />
          <SectionTitle className="party-display">{t("party.playWhoAmI.secretPeekTitle")}</SectionTitle>
          <SectionBody>{t("party.playWhoAmI.revealInstructionA", { team: teamALabel })}</SectionBody>
          <PeekCard className="party-kan-card party-anim-fade-scale-in">
            <PeekCardTeamHeading team="a">{teamALabel}</PeekCardTeamHeading>
            {castByQr ? (
              <>
                <PartyCastQrPanel
                  partyPal={partyPal}
                  url={buildPartyCastRevealUrl("who", {
                    v: 1,
                    shell: "who",
                    rtl: Boolean(i18n.language?.startsWith("ar")),
                    layout: "who_word",
                    h1: teamALabel,
                    w: wA,
                  })}
                />
              </>
            ) : (
              <LongPressWordPeek word={wA} />
            )}
          </PeekCard>
          <PrimaryCta type="button" className="party-btn party-btn-hot" onClick={() => setWordRevealStep("b")}>
            {castByQr ? t("party.cast.continueAfterQr") : t("party.playWhoAmI.donePassToB", { team: teamBLabel })}
          </PrimaryCta>
        </RoundRoot>
      );
    }

    if (wordRevealStep === "b") {
      return (
        <RoundRoot>
          <PartyTopBar
            title={t("party.playWhoAmI.revealTopBar", {
              current: roundCurrent,
              total: roundTotal,
              team: teamBLabel,
            })}
            onBack={() => setWordRevealStep("a")}
          />
          <SectionTitle className="party-display">{t("party.playWhoAmI.secretPeekTitle")}</SectionTitle>
          <SectionBody>{t("party.playWhoAmI.revealInstructionB", { team: teamBLabel })}</SectionBody>
          <PeekCard className="party-kan-card party-anim-fade-scale-in">
            <PeekCardTeamHeading team="b">{teamBLabel}</PeekCardTeamHeading>
            {castByQr ? (
              <>
                <PartyCastQrPanel
                  partyPal={partyPal}
                  url={buildPartyCastRevealUrl("who", {
                    v: 1,
                    shell: "who",
                    rtl: Boolean(i18n.language?.startsWith("ar")),
                    layout: "who_word",
                    h1: teamBLabel,
                    w: wB,
                  })}
                />
              </>
            ) : (
              <LongPressWordPeek word={wB} />
            )}
          </PeekCard>
          <PrimaryCta type="button" className="party-btn party-btn-hot" onClick={() => setWordRevealStep("bridge")}>
            {castByQr ? t("party.cast.continueAfterQr") : t("party.playWhoAmI.doneBothRevealed")}
          </PrimaryCta>
        </RoundRoot>
      );
    }

    return (
      <RoundRoot>
        <PartyTopBar
          title={t("party.playWhoAmI.questionsTopBar", { current: roundCurrent, total: roundTotal })}
          onBack={() => setWordRevealStep("b")}
        />
        <SectionTitle className="party-display">{t("party.playWhoAmI.questionsTitle")}</SectionTitle>
        <SectionBody sx={{ mb: 1.5 }}>{t("party.playWhoAmI.questionsHint")}</SectionBody>
        <PrimaryCta type="button" className="party-btn party-btn-hot" onClick={() => setPhase("score")}>
          {t("party.playWhoAmI.questionsToScoreCta")}
        </PrimaryCta>
      </RoundRoot>
    );
  }

  if (phase === "score") {
    return (
      <RoundRoot>
        <PartyTopBar title={t("party.playWhoAmI.scoreTitleBar")} onBack={() => setPhase("round")} />
        <ScoreHeading>{t("party.playWhoAmI.scoreHeading")}</ScoreHeading>
        <ScoreTeamButton type="button" className="party-btn party-btn-hot" onClick={() => awardRound("a")}>
          {t("party.playWhoAmI.scoreAwardTeam", { team: teamALabel })}
        </ScoreTeamButton>
        <ScoreTeamButton type="button" className="party-btn party-btn-paper" onClick={() => awardRound("b")}>
          {t("party.playWhoAmI.scoreAwardTeam", { team: teamBLabel })}
        </ScoreTeamButton>
      </RoundRoot>
    );
  }

  if (phase === "end") {
    const isTie = scoreA === scoreB;
    const winnerIsA = scoreA > scoreB;
    const winnerLabel = isTie ? null : winnerIsA ? teamALabel : teamBLabel;
    const finalA: FinalPanelVariant = !isTie && winnerIsA ? "winnerA" : "loserOrTieA";
    const finalB: FinalPanelVariant = !isTie && !winnerIsA ? "winnerB" : "loserOrTieB";

    return (
      <EndRoot>
        <PartyTopBar title={t("party.playWhoAmI.endTitleBar")} onBack={() => navigate("/party/detail/who")} />
        <MascotWrap>
          <PartyMascot shellId="who" size={isTie ? 96 : 120} />
        </MascotWrap>

        {isTie ? (
          <>
            <TieOverline className="party-mono">{t("party.playWhoAmI.tieOverline")}</TieOverline>
            <TieTitle className="party-display">{t("party.playWhoAmI.tieTitle")}</TieTitle>
          </>
        ) : (
          <>
            <WinnerKicker className="party-mono">{t("party.playWhoAmI.winnerKicker")}</WinnerKicker>
            <WinnerName className="party-display party-anim-fade-scale-in" winnerIsA={winnerIsA}>
              {winnerLabel}
            </WinnerName>
            <WinnerSub className="party-mono">{t("party.playWhoAmI.winnerSub")}</WinnerSub>
          </>
        )}

        <FinalScoreGrid>
          <FinalScoreCell panelVariant={finalA}>
            <FinalTeamTitle className="party-mono">{t("party.playWhoAmI.teamAShort")}</FinalTeamTitle>
            <FinalTeamName>{teamALabel}</FinalTeamName>
            <FinalScoreNumber className="party-display" team="a">
              {scoreA}
            </FinalScoreNumber>
            <FinalPointsLabel className="party-mono">
              {scoreA === 1 ? t("party.playWhoAmI.point") : t("party.playWhoAmI.points")}
            </FinalPointsLabel>
          </FinalScoreCell>
          <ColonWrap>
            <ScoreColon className="party-display">:</ScoreColon>
          </ColonWrap>
          <FinalScoreCell panelVariant={finalB}>
            <FinalTeamTitle className="party-mono">{t("party.playWhoAmI.teamBShort")}</FinalTeamTitle>
            <FinalTeamName>{teamBLabel}</FinalTeamName>
            <FinalScoreNumber className="party-display" team="b">
              {scoreB}
            </FinalScoreNumber>
            <FinalPointsLabel className="party-mono">
              {scoreB === 1 ? t("party.playWhoAmI.point") : t("party.playWhoAmI.points")}
            </FinalPointsLabel>
          </FinalScoreCell>
        </FinalScoreGrid>

        <PartyShareResultButton
          className="party-btn party-btn-paper"
          sx={{ width: "100%", maxWidth: 420, mt: 2 }}
          model={{
            shellId: "who",
            modeTitle: shareModeTitle,
            resultTitle: i18n.language?.startsWith("ar") ? "النتيجة" : "Result",
            hero: isTie ? (i18n.language?.startsWith("ar") ? "تعادل!" : "Tie!") : String(winnerLabel ?? ""),
            sub: isTie ? undefined : i18n.language?.startsWith("ar") ? "فاز/ت بالجولة" : "Won the round",
            scores: [
              { label: teamALabel, value: scoreA, color: "var(--mint)", highlight: !isTie && winnerIsA },
              { label: teamBLabel, value: scoreB, color: "var(--gold)", highlight: !isTie && !winnerIsA },
            ],
          }}
        />

        <HomeButton type="button" className="party-btn party-btn-hot" onClick={() => navigate("/")}>
          {t("party.playWhoAmI.home")}
        </HomeButton>
      </EndRoot>
    );
  }

  return null;
}
